import {
  Box,
  Divider,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Button } from "components/button";
import { useGlobalStore } from "controller";
import { useGetHomeCalendar } from "controller/hooks";
import type { Dayjs } from "dayjs";
import { useAtomValue } from "jotai";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  calendarAnchorDateAtom,
  calendarSelectedDateAtom,
  DATE_KEY_FORMAT,
  mspDay,
  mspInstant,
  mspNow,
} from "../store";
import type { CalendarEvent, CalendarEventGroup } from "../types";
import { CalendarEmptyState } from "./components/CalendarEmptyState";
import { CalendarHeader } from "./components/CalendarHeader";
import { EventGroup } from "./components/EventGroup";
import { WeekStrip } from "./components/WeekStrip";
import { mapCalendarEvents } from "./data/mapCalendarEvents";

/** Placeholder rows shown while the first week of a range loads. */
const SKELETON_ROWS = ["s1", "s2", "s3", "s4"];

/** Breathing room left above a day heading once it has been scrolled to. */
const SCROLL_TOP_PADDING = 16;

/** Vertical padding of the scroll container, in px. */
const LIST_PADDING_Y = 12;

/** The seven days (Sun→Sat) of the week the anchor date falls in. */
const getWeekDays = (anchor: Dayjs): Dayjs[] => {
  const start = anchor.startOf("week");
  return Array.from({ length: 7 }, (_, index) => start.add(index, "day"));
};

/** Days the event list covers — the strip's week plus the one after it. */
const LIST_DAYS = 14;

/** The days the list buckets events into, starting at the strip's week. */
const getListDays = (anchor: Dayjs): Dayjs[] => {
  const start = anchor.startOf("week");
  return Array.from({ length: LIST_DAYS }, (_, index) =>
    start.add(index, "day"),
  );
};

/** "Today" / "Tomorrow" for the next two days, a formatted date after that. */
const getGroupLabel = (date: Dayjs, timezone?: string): string => {
  const today = mspNow(timezone).startOf("day");
  if (date.isSame(today, "day")) return "Today";
  if (date.isSame(today.add(1, "day"), "day")) return "Tomorrow";
  return date.format("ddd, DD MMM");
};

/**
 * Buckets events into the given days, keeping day order and sorting each day by
 * start time. Days are the MSP's, not the browser's, so an event late in the
 * evening there never slips into the neighbouring day. Days without events are
 * dropped.
 */
const groupEventsByDay = (
  events: CalendarEvent[],
  days: Dayjs[],
  timezone?: string,
): CalendarEventGroup[] => {
  const byDate = new Map<string, CalendarEvent[]>();

  for (const event of events) {
    const key = mspInstant(event.startAt, timezone).format(DATE_KEY_FORMAT);
    const bucket = byDate.get(key);
    if (bucket) {
      bucket.push(event);
    } else {
      byDate.set(key, [event]);
    }
  }

  return days.reduce<CalendarEventGroup[]>((groups, day) => {
    const date = day.format(DATE_KEY_FORMAT);
    const dayEvents = byDate.get(date);
    if (!dayEvents?.length) return groups;

    groups.push({
      date,
      label: getGroupLabel(day, timezone),
      events: [...dayEvents].sort(
        (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
      ),
    });
    return groups;
  }, []);
};

export const CalendarSection = () => {
  const theme = useTheme();
  const timezone = useGlobalStore((state) => state.timezone);
  const anchorDate = useAtomValue(calendarAnchorDateAtom);
  const selectedDate = useAtomValue(calendarSelectedDateAtom);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const didInitialScroll = useRef(false);
  const [tailSpacer, setTailSpacer] = useState(0);

  // The strip stays a single week; the list below it runs two, so days past
  // Saturday are still reachable by scrolling.
  const weekDays = useMemo(
    () => getWeekDays(mspDay(anchorDate, timezone)),
    [anchorDate, timezone],
  );
  const listDays = useMemo(
    () => getListDays(mspDay(anchorDate, timezone)),
    [anchorDate, timezone],
  );

  // The MSP's fortnight bounds as UTC ISO — the same `startOf("week")` the strip
  // renders, so the range requested and the range shown can never drift apart.
  const range = useMemo(() => {
    const start = mspDay(anchorDate, timezone).startOf("week");
    return {
      start: start.toISOString(),
      end: start.add(LIST_DAYS, "day").toISOString(), // exclusive
    };
  }, [anchorDate, timezone]);

  const { data, isFetching, isError, refetch } = useGetHomeCalendar({
    start: range.start,
    end: range.end,
    calendar_type: "my-calendar",
  });

  const events = useMemo(() => mapCalendarEvents(data?.events), [data?.events]);
  const groups = useMemo(
    () => groupEventsByDay(events, listDays, timezone),
    [events, listDays, timezone],
  );

  // `keepPreviousData` holds the last week on screen while the next one loads,
  // so the skeleton and the error state only take over when there is nothing
  // left to show.
  const showSkeleton = isFetching && !groups.length;
  const showError = isError && !isFetching && !groups.length;
  const showEmpty = !(showSkeleton || showError || groups.length);

  // The selected day often has no events — today included — so scrolling falls
  // back to the next day that does, and to the last group when the selection is
  // past everything. Date keys are YYYY-MM-DD, so string order is date order.
  const scrollTargetDate = useMemo(() => {
    if (!groups.length) return null;
    const upcoming = groups.find((group) => group.date >= selectedDate);
    return (upcoming ?? groups[groups.length - 1]).date;
  }, [groups, selectedDate]);

  // Without extra room under the last group it can never reach the top of the
  // viewport — the scroll clamps and the last day of the week stays wherever it
  // sits. The spacer is exactly the shortfall, so shorter weeks get no dead
  // space at all. Measured in a layout effect so it is in place before the
  // scroll effect below reads `offsetTop`.
  useLayoutEffect(() => {
    const container = scrollRef.current;
    const lastDate = groups[groups.length - 1]?.date;
    const lastGroup = lastDate ? groupRefs.current[lastDate] : null;

    if (!(container && lastGroup)) {
      setTailSpacer(0);
      return;
    }

    const measure = () => {
      setTailSpacer(
        Math.max(
          0,
          container.clientHeight -
            lastGroup.offsetHeight -
            SCROLL_TOP_PADDING -
            LIST_PADDING_Y,
        ),
      );
    };

    measure();

    // The container tracks the viewport and the last group grows as avatars
    // load, so both need watching.
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(lastGroup);
    return () => observer.disconnect();
  }, [groups]);

  // Picking a day in the strip scrolls its group to the top of the list rather
  // than filtering, so the rest of the week stays reachable.
  useEffect(() => {
    if (!scrollTargetDate) return;

    const container = scrollRef.current;
    const target = groupRefs.current[scrollTargetDate];
    if (!(container && target)) return;

    // The first positioning after a fetch happens on the commit that mounts the
    // list, while the container is still growing into its flex row — a smooth
    // scroll started there gets clamped to 0 and never lands. Defer a frame and
    // jump instantly; only later day picks are worth animating.
    const top = Math.max(0, target.offsetTop - SCROLL_TOP_PADDING);

    if (didInitialScroll.current) {
      container.scrollTo({ top, behavior: "smooth" });
      return;
    }

    const frame = requestAnimationFrame(() => {
      didInitialScroll.current = true;
      container.scrollTo({ top, behavior: "auto" });
    });
    return () => cancelAnimationFrame(frame);
  }, [scrollTargetDate]);

  return (
    <Stack
      sx={{
        height: "100%",
        overflow: "hidden",
        backgroundColor: theme.palette.neutral.main,
        borderRadius: "8px",
      }}
    >
      {/* Sticky block: header + week navigation */}
      <Stack sx={{ flexShrink: 0, pb: "12px", gap: "16px" }}>
        <CalendarHeader />
        <WeekStrip weekDays={weekDays} selectedDate={selectedDate} />
      </Stack>
      <Divider sx={{ borderColor: theme.palette.neutral["010"] }} />

      {/* Scrollable block: grouped event list */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          position: "relative",
          px: "16px",
          py: `${LIST_PADDING_Y}px`,
        }}
      >
        {showSkeleton ? (
          <Stack gap="8px">
            {SKELETON_ROWS.map((row) => (
              <Skeleton
                key={row}
                variant="rounded"
                height={62}
                sx={{ borderRadius: "8px" }}
              />
            ))}
          </Stack>
        ) : null}

        {showError ? (
          <Stack alignItems="center" gap="8px" sx={{ py: "32px" }}>
            <Typography
              variant="body-small"
              sx={{ color: theme.palette.lightBg.low }}
            >
              Couldn't load your calendar
            </Typography>
            <Button variant="secondary" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          </Stack>
        ) : null}

        {groups.length ? (
          <>
            <Stack gap="12px">
              {groups.map((group) => (
                <EventGroup
                  key={group.date}
                  group={group}
                  ref={(node) => {
                    groupRefs.current[group.date] = node;
                  }}
                />
              ))}
            </Stack>
            {tailSpacer ? (
              <Box aria-hidden="true" sx={{ height: `${tailSpacer}px` }} />
            ) : null}
          </>
        ) : null}

        {showEmpty ? <CalendarEmptyState /> : null}
      </Box>
    </Stack>
  );
};
